import { getServerSideFallbackInfo } from '../llm/model/server-side-fallback';
import { getTransformedToolPayload, hasTransformedToolPayload } from '../tools/payload-transform';
import type { ToolPayloadTransformPhase } from '../tools/types';
import type { TokenUsage } from './types';

/**
 * Pure transforms shared by the run engine for folding raw agent-stream chunk
 * payloads into the `MastraDBMessage` shape a Session renders, plus terminal
 * finish-reason and server-side-fallback diagnostics. They hold no
 * AgentController or Session state, so they live in their own module.
 */

export function getDisplayTransform(metadata: unknown, phase: ToolPayloadTransformPhase, fallback: unknown) {
  const transform = getTransformedToolPayload(metadata, 'display', phase);
  return hasTransformedToolPayload(transform) ? transform.transformed : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getAnthropicStopDetails(providerMetadata: unknown): Record<string, unknown> | undefined {
  if (!isRecord(providerMetadata)) {
    return undefined;
  }
  const anthropic = providerMetadata.anthropic;
  if (!isRecord(anthropic)) {
    return undefined;
  }
  const stopDetails = anthropic.stopDetails;
  return isRecord(stopDetails) ? stopDetails : undefined;
}

/**
 * Map a non-success terminal finish reason (content-filter, error, length) to a
 * user-facing message, or `undefined` for success reasons. A non-success finish
 * must become an explicit terminal error rather than a silent `complete`.
 */
export function describeNonSuccessFinishReason(reason: string, providerMetadata: unknown): string | undefined {
  switch (reason) {
    case 'content-filter': {
      const stopDetails = getAnthropicStopDetails(providerMetadata);
      const explanation =
        stopDetails && typeof stopDetails.explanation === 'string' ? stopDetails.explanation : undefined;
      const category = stopDetails && typeof stopDetails.category === 'string' ? stopDetails.category : undefined;
      const detail = explanation ?? (category ? `category: ${category}` : undefined);
      return detail ? `The model stopped on a content filter (${detail}).` : 'The model stopped on a content filter.';
    }
    case 'error':
      return 'The model stream ended with an error before producing a final response.';
    case 'length':
      return 'The model stopped because it reached its maximum output length before finishing.';
    default:
      return undefined;
  }
}

/**
 * Build a user-facing notice when a turn was served by an Anthropic server-side
 * fallback model instead of the primary model. Without a notice the user has no
 * way to tell the response did not come from the model they selected.
 */
export function describeServerSideFallback(providerMetadata: unknown): string | undefined {
  const fallback = getServerSideFallbackInfo(providerMetadata);
  if (!fallback) {
    return undefined;
  }
  return fallback.model
    ? `The selected model declined this turn; the response was generated by fallback model ${fallback.model}.`
    : 'The selected model declined this turn; the response was generated by a fallback model.';
}

/** Coerce a usage field to a finite number, accepting numeric strings. */
export function getUsageNumber(usage: Record<string, unknown>, key: string): number | undefined {
  const value = usage[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }
  return undefined;
}

/** Fold an optional usage field into a tally when present. */
export function addOptionalUsageField(
  usage: TokenUsage,
  key: keyof Pick<TokenUsage, 'reasoningTokens' | 'cachedInputTokens' | 'cacheCreationInputTokens'>,
  value: number | undefined,
): void {
  if (value !== undefined) {
    usage[key] = (usage[key] ?? 0) + value;
  }
}
